// src/pages/AdminExpensesPage.js
// Accounting review page for driver-submitted expenses.
// Backed by /api/expenses — see backend/routes/expenses.js.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../scripts/api";
import {
  Receipt, Search, Download, RefreshCw, X, CheckCircle2, AlertCircle,
  Image as ImageIcon, User, Package, Calendar,
} from "lucide-react";

const CATEGORY_META = {
  fuel:    { icon: "⛽", color: "#f59e0b" },
  meal:    { icon: "🍔", color: "#ef4444" },
  hotel:   { icon: "🏨", color: "#8b5cf6" },
  tolls:   { icon: "🛣️", color: "#3b82f6" },
  repair:  { icon: "🔧", color: "#dc2626" },
  parking: { icon: "🅿️", color: "#0891b2" },
  other:   { icon: "📎", color: "#6b7280" },
};

function todayISO() { return new Date().toISOString().slice(0, 10); }
function thirtyDaysAgoISO() { return new Date(Date.now() - 30*24*60*60*1000).toISOString().slice(0, 10); }

export default function AdminExpensesPage() {
  // Filters
  const [driverId,  setDriverId]  = useState("");
  const [category,  setCategory]  = useState("");
  const [status,    setStatus]    = useState("");
  const [from,      setFrom]      = useState(() => thirtyDaysAgoISO());
  const [to,        setTo]        = useState(() => todayISO());
  const [search,    setSearch]    = useState("");

  const [drivers,   setDrivers]   = useState([]);
  const [expenses,  setExpenses]  = useState([]);
  const [summary,   setSummary]   = useState({ total: 0, total_amount: 0, pending: 0, unapproved: 0 });
  const [loading,   setLoading]   = useState(false);
  const [msg,       setMsg]       = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [photoViewer, setPhotoViewer] = useState(null); // { paths, index }

  const loadDrivers = useCallback(async () => {
    try {
      const { data } = await api.get("/driver");
      setDrivers(Array.isArray(data) ? data : []);
    } catch (_) {}
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setMsg("");
    try {
      const params = {};
      if (driverId)  params.driver_id = driverId;
      if (category)  params.category  = category;
      if (status)    params.status    = status;
      if (from)      params.from      = from;
      if (to)        params.to        = to;
      if (search.trim()) params.search = search.trim();
      const { data } = await api.get("/expenses", { params });
      setExpenses(data?.expenses || []);
      setSummary({
        total:        data?.total        || 0,
        total_amount: Number(data?.total_amount || 0),
        pending:      data?.pending      || 0,
        unapproved:   data?.unapproved   || 0,
      });
    } catch (e) {
      setMsg(e?.response?.data?.error || "Failed to load expenses");
      setExpenses([]);
    } finally { setLoading(false); }
  }, [driverId, category, status, from, to, search]);

  useEffect(() => { loadDrivers(); }, [loadDrivers]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (driverId) params.set("driver_id", driverId);
    if (category) params.set("category", category);
    if (status)   params.set("status", status);
    if (from)     params.set("from", from);
    if (to)       params.set("to", to);
    if (search.trim()) params.set("search", search.trim());
    // Use api baseURL + add auth token manually — window.open doesn't pass headers,
    // so we use a token query param that the backend accepts (fallback) OR open via fetch+blob.
    (async () => {
      try {
        const res = await api.get(`/expenses.csv?${params.toString()}`, { responseType: "blob" });
        const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `expenses-${todayISO()}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch (e) {
        setMsg(e?.response?.data?.error || "Export failed");
      }
    })();
  };

  const selected = useMemo(() => expenses.find(e => e.id === selectedId) || null, [expenses, selectedId]);

  return (
    <div className="container">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 900 }}>
            <Receipt size={22} style={{ verticalAlign: -4, marginRight: 8 }} />
            Driver Expenses
          </h2>
          <div className="muted" style={{ marginTop: 4 }}>
            Review driver-submitted receipts, mark hardcopy received, approve for accounting.
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn-outline" onClick={load} disabled={loading}>
            <RefreshCw size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
            Refresh
          </button>
          <button className="btn" onClick={exportCsv} disabled={loading || expenses.length === 0}>
            <Download size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="row" style={{ gap: 10, marginTop: 14, flexWrap: "wrap" }}>
        <SummaryCard label="Receipts" value={summary.total} />
        <SummaryCard label="Total" value={`$${summary.total_amount.toFixed(2)}`} color="#16a34a" />
        <SummaryCard label="Hardcopy pending" value={summary.pending} color="#f59e0b" />
        <SummaryCard label="Unapproved" value={summary.unapproved} color="#dc2626" />
      </div>

      {/* Filter bar */}
      <div style={{
        marginTop: 12, padding: 10, background: "var(--card-bg)",
        border: "1px solid var(--card-border)", borderRadius: 12,
        display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center",
      }}>
        <select className="input" value={driverId} onChange={e => setDriverId(e.target.value)} style={{ flex: "1 1 160px", maxWidth: 240 }}>
          <option value="">All drivers</option>
          {drivers.map(d => (
            <option key={d.DriverId} value={d.DriverId}>{d.FullName || `#${d.DriverId}`}</option>
          ))}
        </select>
        <select className="input" value={category} onChange={e => setCategory(e.target.value)} style={{ flex: "1 1 120px", maxWidth: 180 }}>
          <option value="">All categories</option>
          {Object.keys(CATEGORY_META).map(k => (
            <option key={k} value={k}>{CATEGORY_META[k].icon} {k}</option>
          ))}
        </select>
        <select className="input" value={status} onChange={e => setStatus(e.target.value)} style={{ flex: "1 1 140px", maxWidth: 180 }}>
          <option value="">All status</option>
          <option value="pending">Hardcopy pending</option>
          <option value="received">Hardcopy received</option>
          <option value="approved">Approved</option>
          <option value="unapproved">Unapproved</option>
        </select>
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          <input className="input" type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ maxWidth: 150 }} />
          <span className="muted">→</span>
          <input className="input" type="date" value={to} onChange={e => setTo(e.target.value)} style={{ maxWidth: 150 }} />
        </div>
        <div style={{ position: "relative", flex: "1 1 180px", maxWidth: 260 }}>
          <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", opacity: 0.5 }} />
          <input
            className="input" placeholder="Vendor, ROWO, driver…"
            value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") load(); }}
            style={{ width: "100%", paddingLeft: 30 }}
          />
        </div>
      </div>

      {msg && <div className="card" style={{ marginTop: 10, color: "#dc2626" }}>{msg}</div>}

      {/* Table */}
      <div className="card" style={{ marginTop: 12, padding: 0, overflow: "hidden" }}>
        {loading && <div className="muted" style={{ padding: 14 }}>Loading…</div>}
        {!loading && expenses.length === 0 && <div className="muted" style={{ padding: 14 }}>No expenses match your filters.</div>}
        {!loading && expenses.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "rgba(127,127,127,0.06)" }}>
                  <Th>Date</Th><Th>Driver</Th><Th>Unit / Move</Th><Th>Category</Th>
                  <Th style={{ textAlign: "right" }}>Amount</Th>
                  <Th>Vendor</Th><Th>Status</Th><Th>Photos</Th>
                </tr>
              </thead>
              <tbody>
                {expenses.map(e => {
                  const cat = CATEGORY_META[e.category] || CATEGORY_META.other;
                  let photoCount = 0;
                  try { photoCount = JSON.parse(e.photo_paths || "[]").length; } catch (_) {}
                  return (
                    <tr key={e.id}
                        onClick={() => setSelectedId(e.id)}
                        style={{ cursor: "pointer", borderTop: "1px solid var(--card-border)", background: selectedId === e.id ? "rgba(59,130,246,0.08)" : undefined }}
                    >
                      <Td>{e.expense_date}</Td>
                      <Td>{e.driver_name || `#${e.driver_id}`}</Td>
                      <Td>
                        {e.rowo ? <span style={{ fontWeight: 700 }}>{e.rowo}</span> : <span className="muted">—</span>}
                        {e.trip_id && <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>· Move #{e.trip_id}</span>}
                      </Td>
                      <Td>
                        <span style={{ padding: "2px 8px", borderRadius: 99, background: cat.color + "20", color: cat.color, fontWeight: 700, fontSize: 11 }}>
                          {cat.icon} {e.category}
                        </span>
                      </Td>
                      <Td style={{ textAlign: "right", fontWeight: 800 }}>
                        ${Number(e.amount).toFixed(2)} <span className="muted" style={{ fontWeight: 400, fontSize: 11 }}>{e.currency}</span>
                      </Td>
                      <Td>{e.vendor || "—"}</Td>
                      <Td>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          {e.hardcopy_received
                            ? <StatusPill bg="#dcfce7" color="#166534">✓ Hardcopy</StatusPill>
                            : <StatusPill bg="#fef3c7" color="#92400e">Pending</StatusPill>}
                          {e.approved_at && <StatusPill bg="#dcfce7" color="#166534">✓ Approved</StatusPill>}
                        </div>
                      </Td>
                      <Td>
                        {photoCount > 0 ? <span className="muted">📷 {photoCount}</span> : <span className="muted">—</span>}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <ExpenseDrawer
          expense={selected}
          onClose={() => setSelectedId(null)}
          onChanged={() => { load(); }}
          onOpenPhoto={(paths, index) => setPhotoViewer({ paths, index })}
        />
      )}

      {photoViewer && (
        <PhotoViewer
          paths={photoViewer.paths}
          index={photoViewer.index}
          onClose={() => setPhotoViewer(null)}
          onIndex={(i) => setPhotoViewer(v => ({ ...v, index: i }))}
        />
      )}
    </div>
  );
}

const Th = ({ children, style }) => (
  <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, opacity: 0.6, ...style }}>
    {children}
  </th>
);
const Td = ({ children, style }) => (
  <td style={{ padding: "10px 12px", ...style }}>{children}</td>
);
const StatusPill = ({ bg, color, children }) => (
  <span style={{ background: bg, color, padding: "2px 8px", borderRadius: 99, fontSize: 10, fontWeight: 700, letterSpacing: 0.3 }}>
    {children}
  </span>
);

function SummaryCard({ label, value, color = "#3b82f6" }) {
  return (
    <div className="card" style={{ flex: "1 1 160px", minWidth: 140, padding: 14 }}>
      <div className="muted" style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 900, color, marginTop: 4 }}>{value}</div>
    </div>
  );
}

// ── Side drawer: expense detail + toggles ───────────────────────────────────
function ExpenseDrawer({ expense, onClose, onChanged, onOpenPhoto }) {
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState("");
  const [internalNotes, setInternalNotes] = useState(expense.notes_internal || "");

  let photos = [];
  try { photos = JSON.parse(expense.photo_paths || "[]"); } catch (_) {}
  const cat = CATEGORY_META[expense.category] || CATEGORY_META.other;

  const patch = async (body) => {
    setErr(""); setBusy(true);
    try {
      await api.patch(`/expenses/${expense.id}`, body);
      onChanged?.();
    } catch (e) {
      setErr(e?.response?.data?.error || "Update failed");
    } finally { setBusy(false); }
  };

  const saveInternal = () => patch({ notes_internal: internalNotes });

  const remove = async () => {
    if (!window.confirm("Delete this expense? This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.delete(`/expenses/${expense.id}`);
      onChanged?.(); onClose();
    } catch (e) {
      setErr(e?.response?.data?.error || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000,
      display: "flex", justifyContent: "flex-end",
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        width: "100%", maxWidth: 520, height: "100%",
        background: "var(--card-bg)", borderLeft: "1px solid var(--card-border)",
        overflowY: "auto", padding: 20,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontWeight: 900, fontSize: 20 }}>Expense #{expense.id}</div>
          <button className="btn-outline" onClick={onClose} style={{ padding: 6 }}><X size={16} /></button>
        </div>

        <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 14 }}>
          <div style={{ fontSize: 36, fontWeight: 900, color: cat.color }}>
            ${Number(expense.amount).toFixed(2)}
          </div>
          <div className="muted" style={{ fontSize: 13 }}>{expense.currency}</div>
          <span style={{ marginLeft: "auto", padding: "4px 12px", borderRadius: 99, background: cat.color + "20", color: cat.color, fontWeight: 700, fontSize: 13 }}>
            {cat.icon} {expense.category}
          </span>
        </div>

        <div style={{ display: "grid", gap: 8, fontSize: 13, marginBottom: 14 }}>
          <KeyVal icon={User} label="Driver" value={expense.driver_name || `#${expense.driver_id}`} />
          {expense.rowo    && <KeyVal icon={Package}  label="Unit"      value={`${expense.rowo}${expense.stock_po ? ` · ${expense.stock_po}` : ""}`} />}
          {expense.trip_id && <KeyVal icon={Package}  label="Move"      value={`#${expense.trip_id}${expense.destination ? ` → ${expense.destination}` : ""}`} />}
          <KeyVal icon={Calendar} label="Date"     value={expense.expense_date} />
          <KeyVal            label="Vendor"   value={expense.vendor || "—"} />
          <KeyVal            label="Submitted" value={`${expense.submitted_at?.slice(0, 16).replace('T', ' ') || '—'} via ${expense.submitted_from || '—'}`} />
          {expense.notes && <KeyVal label="Notes" value={expense.notes} />}
        </div>

        {/* Photos */}
        {photos.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6, opacity: 0.6 }}>
              <ImageIcon size={12} style={{ verticalAlign: -1, marginRight: 4 }} />
              Photos ({photos.length})
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {photos.map((p, i) => (
                <img
                  key={i} src={p} alt="" onClick={() => onOpenPhoto(photos, i)}
                  style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 6, border: "1px solid var(--card-border)", cursor: "zoom-in" }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Admin actions */}
        <div style={{ display: "grid", gap: 10, marginBottom: 14 }}>
          <button
            className={expense.hardcopy_received ? "btn-outline" : "btn"}
            onClick={() => patch({ hardcopy_received: !expense.hardcopy_received })}
            disabled={busy}
            style={{ padding: "12px 14px", fontWeight: 800 }}
          >
            {expense.hardcopy_received
              ? <><CheckCircle2 size={14} style={{ marginRight: 6, verticalAlign: -2 }} />Hardcopy Received — click to undo</>
              : <><AlertCircle size={14} style={{ marginRight: 6, verticalAlign: -2 }} />Mark Hardcopy Received</>}
          </button>
          <button
            className={expense.approved_at ? "btn-outline" : "btn"}
            onClick={() => patch({ approved: !expense.approved_at })}
            disabled={busy}
            style={{ padding: "12px 14px", fontWeight: 800, background: expense.approved_at ? undefined : "#16a34a", color: expense.approved_at ? undefined : "#fff" }}
          >
            {expense.approved_at
              ? `✓ Approved ${expense.approved_at.slice(0, 10)} — click to un-approve`
              : "Approve for Accounting"}
          </button>
        </div>

        {/* Internal notes */}
        <div style={{ marginBottom: 14 }}>
          <label className="muted" style={{ fontSize: 12, fontWeight: 700 }}>Internal notes (accounting only)</label>
          <textarea
            className="input" style={{ width: "100%", minHeight: 80, marginTop: 6 }}
            value={internalNotes} onChange={e => setInternalNotes(e.target.value)}
            placeholder="Not shown to driver."
          />
          <button className="btn-outline" style={{ marginTop: 6 }} onClick={saveInternal} disabled={busy}>
            Save internal notes
          </button>
        </div>

        {err && <div style={{ color: "#dc2626", marginBottom: 10 }}>{err}</div>}

        <button className="btn-outline" onClick={remove} disabled={busy} style={{ color: "#dc2626", borderColor: "#fca5a5" }}>
          Delete Expense
        </button>
      </div>
    </div>
  );
}

function KeyVal({ icon: Icon, label, value }) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
      <div style={{ flex: "0 0 110px", opacity: 0.55, fontSize: 12, fontWeight: 700 }}>
        {Icon && <Icon size={12} style={{ verticalAlign: -1, marginRight: 4 }} />}
        {label}
      </div>
      <div style={{ flex: 1, fontWeight: 600, whiteSpace: "pre-wrap" }}>{value}</div>
    </div>
  );
}

// ── Photo zoom viewer ───────────────────────────────────────────────────────
function PhotoViewer({ paths, index, onClose, onIndex }) {
  const i = Math.max(0, Math.min(index, paths.length - 1));
  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.9)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={onClose}
    >
      <button onClick={onClose} style={{ position: "absolute", top: 14, right: 14, padding: 10, background: "rgba(255,255,255,0.15)", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer" }}>
        <X size={20} />
      </button>
      <img src={paths[i]} alt="" style={{ maxWidth: "95vw", maxHeight: "90vh", objectFit: "contain" }} />
      {paths.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onIndex((i - 1 + paths.length) % paths.length); }}
            style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", padding: 14, background: "rgba(255,255,255,0.15)", color: "#fff", border: "none", borderRadius: "50%", cursor: "pointer", fontSize: 20 }}
          >‹</button>
          <button
            onClick={(e) => { e.stopPropagation(); onIndex((i + 1) % paths.length); }}
            style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", padding: 14, background: "rgba(255,255,255,0.15)", color: "#fff", border: "none", borderRadius: "50%", cursor: "pointer", fontSize: 20 }}
          >›</button>
          <div style={{ position: "absolute", bottom: 20, left: "50%", transform: "translateX(-50%)", color: "#fff", fontSize: 13, opacity: 0.7 }}>
            {i + 1} / {paths.length}
          </div>
        </>
      )}
    </div>
  );
}
